/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/** @jsx jsx */
import { jsx } from "@emotion/core"
import { Drawer, Button } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { FC, useEffect, useRef, useState } from "react"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { actionCreator } from "store/store"
import {
  LanguageFullName,
  MenuTranslationRequestData,
  MenuTranslationSetting,
  TranslationDetail,
  TranslationFailedResponse,
  TranslationSetting,
} from "types/models/menu"
import { TextType, TranslationType } from "constants/menu"
import { useDispatch } from "react-redux"
import {
  descriptionTranslationHelper,
  isAutoTranslationQueueExists,
  nameTranslationHelper,
} from "utils/translation_helper"
import { camelCase } from "lodash"
import { hasFormErrors } from "utils/form_helper"
import { ChevronLeftUniversal } from "@duxton-web/icons"
import { TroyWebTracker } from "utils/tracking"
import { EventActionEng, EventCategoryEng } from "constants/tracking"
import styles from "./TranslationDrawer.modules.css"
import { TranslationDrawerForm } from "./TranslationDrawerForm"

interface TranslationDrawerProps extends FormComponentProps {
  visible: boolean
  translationType: TranslationType
  merchantID: string
  merchantGroupID: string
  menuGroupID: string
  menuTranslationSetting?: MenuTranslationSetting
  nameTranslation?: TranslationSetting
  resetNameTranslation?: TranslationSetting
  descriptionTranslation?: TranslationSetting
  resetDescriptionTranslation?: TranslationSetting
  defaultValue: string
  defaultValueDescription?: string
  onClose(): void
  onSave(formValues?: TranslationDetail): void
  destinationKey?: string
  disabled?: boolean
  isAutoTranslationEnabled: boolean
  isEdit?: boolean
  autoTranslationQueue?: Set<string>
}

export const TranslationDrawerComponent: FC<TranslationDrawerProps> = ({
  visible,
  onClose,
  onSave,
  translationType,
  defaultValue,
  defaultValueDescription,
  nameTranslation,
  resetNameTranslation,
  descriptionTranslation,
  resetDescriptionTranslation,
  menuTranslationSetting,
  destinationKey,
  merchantID,
  merchantGroupID,
  menuGroupID,
  form,
  disabled,
  isAutoTranslationEnabled,
  isEdit,
  autoTranslationQueue,
}: TranslationDrawerProps) => {
  const dispatch = useDispatch()
  const [firstLoad, setFirstLoad] = useState(true)
  const [firstLoadDesc, setFirstLoadDesc] = useState(true)
  const { setFieldsValue, setFields, validateFields } = form

  const defaultValueRef = useRef(defaultValue)
  const defaultValueDescriptionRef = useRef(defaultValueDescription)

  const textMap = useTranslationsMapTyped({
    confirm: "common.confirm",
    explanationHeader: "menuTranslation.explanationHeader",
    itemLabel: "menuTranslation.itemLabel",
    categoryLabel: "menuTranslation.categoryLabel",
    optionLabel: "menuTranslation.optionLabel",
    optionGroupLabel: "menuTranslation.optionGroupLabel",
    editTranslation: "menuTranslation.editTranslation",
    errorExceedLimit: "menuTranslation.errorExceedLimit",
    explanationDetail: "menuTranslation.explanationDetail",
    errorRequired: "menuTranslation.errorRequired",
    descriptionLabel: "menu.drawers.item.labels.description",
  })

  const onResetTranslation = (
    position: MenuTranslationRequestData["textType"],
    targetLang: string,
    initialValue?: string,
  ) => {
    const data: MenuTranslationRequestData = {
      textType: position,
      entity: camelCase(
        translationType,
      ) as MenuTranslationRequestData["entity"],
      text: initialValue || "",
      textSourceLang: menuTranslationSetting?.menuDefaultLanguage || "",
      textTargetLang: [targetLang],
      destinationKey,
    }
    dispatch(
      actionCreator.menu.getSingleTranslation({
        data,
        merchantID,
        merchantGroupID,
        menuGroupID,
      }),
    )
  }

  const handleSave = () => {
    validateFields(async (formError: any, formValues: any) => {
      if (!formError) {
        const finalData = {
          ...formValues,
          entityType: camelCase(translationType),
          // entityID: need clarify on what to pass
        }
        const resp: any = await dispatch(
          actionCreator.menu.getVerifyTranslation({
            data: finalData,
            merchantID,
            merchantGroupID,
            menuGroupID,
          }),
        )
        if (resp && resp.type.endsWith("_SUCCESS")) {
          TroyWebTracker.sendEvent({
            category: EventCategoryEng.verifyTranslation,
            action: EventActionEng.verifyTranslationSuccess,
          })
          onSave(formValues)
        } else if (resp && resp.type.endsWith("_FAIL")) {
          const data = resp.error?.response?.data
          const messageError =
            data?.message && typeof data.message !== "string"
              ? JSON.parse(data.message)
              : undefined

          TroyWebTracker.sendEvent({
            category: EventCategoryEng.verifyTranslation,
            action: EventActionEng.verifyTranslationFail,
          })
          messageError?.map((value: TranslationFailedResponse) => {
            let existingValue: string
            let targetForm: string
            if (value.target?.includes("Name")) {
              existingValue = nameTranslationHelper.getTranslation(
                formValues.nameTranslation,
                value.language || "",
              )
              targetForm = nameTranslationHelper.getFormKey(value.language)
            } else {
              existingValue = descriptionTranslationHelper.getTranslation(
                formValues.descriptionTranslation,
                value.language || "",
              )
              targetForm = descriptionTranslationHelper.getFormKey(
                value.language,
              )
            }
            let errorValue: string
            if (value.target?.includes("Exceed")) {
              errorValue = textMap.errorExceedLimit.replace(
                "{x}",
                value.message || "",
              )
            } else if (value.target?.includes("Empty")) {
              errorValue = textMap.errorRequired
            } else {
              errorValue = value.message || value.target
            }

            return setFields({
              [targetForm]: {
                value: existingValue,
                errors: [new Error(errorValue)],
              },
            })
          })
        }
      }
    })
  }

  useEffect(() => {
    defaultValueRef.current = defaultValue
  }, [defaultValue])

  useEffect(() => {
    defaultValueDescriptionRef.current = defaultValueDescription
  }, [defaultValueDescription])

  useEffect(() => {
    if (
      resetNameTranslation?.translation &&
      resetNameTranslation?.originalText === defaultValueRef.current
    ) {
      Object.entries(resetNameTranslation.translation).map(([key, value]) =>
        setFieldsValue({
          [nameTranslationHelper.getFormKey(key)]: value,
        }),
      )
    }
    if (
      resetDescriptionTranslation?.translation &&
      resetDescriptionTranslation?.originalText ===
        defaultValueDescriptionRef.current &&
      translationType === TranslationType.item
    ) {
      Object.entries(resetDescriptionTranslation.translation).map(
        ([key, value]) =>
          setFieldsValue({
            [descriptionTranslationHelper.getFormKey(key)]: value,
          }),
      )
    }
  }, [
    resetDescriptionTranslation,
    resetNameTranslation,
    setFieldsValue,
    translationType,
  ])

  // HINT: Auto Translation on first load in edit category and item, modifier and modifierGroup logic located in handleOnClickEditModifier on ModifierGroups
  useEffect(() => {
    if (
      isAutoTranslationEnabled &&
      isEdit &&
      defaultValue?.trim().length &&
      firstLoad &&
      (translationType === TranslationType.item ||
        translationType === TranslationType.category)
    ) {
      const callingAutoTranslation = menuTranslationSetting?.translationModelSupportedLanguages?.filter(
        value => {
          return !nameTranslation?.translation[value] ? value : null
        },
      )
      if (callingAutoTranslation?.length) {
        const data: MenuTranslationRequestData = {
          textType: "name",
          entity: camelCase(
            translationType,
          ) as MenuTranslationRequestData["entity"],
          text: defaultValue,
          textSourceLang: menuTranslationSetting?.menuDefaultLanguage || "",
          textTargetLang: callingAutoTranslation,
        }
        dispatch(
          actionCreator.menu.updateExistingTranslation({
            data,
            merchantID,
            merchantGroupID,
            menuGroupID,
          }),
        )
      }
      setFirstLoad(false)
    }
  }, [
    defaultValue,
    dispatch,
    firstLoad,
    isAutoTranslationEnabled,
    isEdit,
    menuGroupID,
    menuTranslationSetting,
    merchantID,
    merchantGroupID,
    nameTranslation,
    translationType,
  ])

  // Auto Translation on first load in edit item description
  useEffect(() => {
    if (
      isAutoTranslationEnabled &&
      isEdit &&
      firstLoadDesc &&
      translationType === TranslationType.item &&
      defaultValueDescription?.trim().length
    ) {
      const callingDescAutoTranslation = menuTranslationSetting?.translationModelSupportedLanguages?.filter(
        value => {
          return !descriptionTranslation?.translation[value] ? value : null
        },
      )
      if (callingDescAutoTranslation?.length) {
        const data: MenuTranslationRequestData = {
          textType: "description",
          entity: camelCase(
            translationType,
          ) as MenuTranslationRequestData["entity"],
          text: defaultValueDescription,
          textSourceLang: menuTranslationSetting?.menuDefaultLanguage || "",
          textTargetLang: callingDescAutoTranslation,
        }
        dispatch(
          actionCreator.menu.updateExistingTranslation({
            data,
            merchantID,
            merchantGroupID,
            menuGroupID,
          }),
        )
      }
      setFirstLoadDesc(false)
    }
  }, [
    defaultValueDescription,
    descriptionTranslation,
    dispatch,
    firstLoadDesc,
    isAutoTranslationEnabled,
    isEdit,
    menuGroupID,
    menuTranslationSetting,
    merchantID,
    merchantGroupID,
    translationType,
  ])

  const getFormName = (location: TranslationType): string => {
    switch (location) {
      case TranslationType.item:
        return textMap.itemLabel
      case TranslationType.category:
        return textMap.categoryLabel
      case TranslationType.modifier:
        return textMap.optionLabel
      case TranslationType.modifierGroup:
        return textMap.optionGroupLabel
      default:
        return textMap.itemLabel
    }
  }
  const supportedLanguage =
    menuTranslationSetting?.menuSupportedLanguages?.map(languageKey => {
      return menuTranslationSetting?.languageFullNames?.[
        languageKey as keyof LanguageFullName
      ]
    }) || []

  const isLoadingNameTranslation = (targetLang: keyof LanguageFullName) =>
    isAutoTranslationQueueExists({
      queue: autoTranslationQueue,
      entityType: translationType,
      textType: TextType.name,
      targetLang,
    })
  const isLoadingDescriptionTranslation = (
    targetLang: keyof LanguageFullName,
  ) =>
    isAutoTranslationQueueExists({
      queue: autoTranslationQueue,
      entityType: translationType,
      textType: TextType.description,
      targetLang,
    })

  return (
    <div data-testid="translation-drawer">
      <Drawer
        title={textMap.editTranslation}
        open={visible}
        onClose={onClose}
        width={450}
        destroyOnClose
        closeIcon={<ChevronLeftUniversal />}
      >
        <div className={styles.content}>
          <div className={styles.contentHeader}>
            {textMap.explanationHeader}
            <br />
            <br />
            {textMap.explanationDetail.replace(
              "{language 1}",
              supportedLanguage.join(", "),
            )}
          </div>
          {menuTranslationSetting?.menuSupportedLanguages?.map(
            (key: keyof LanguageFullName) => (
              <div>
                <div className={styles.languageBarrier}>
                  {menuTranslationSetting.languageFullNames?.[
                    key
                  ]?.toUpperCase()}
                </div>
                <div className={styles.translationForm}>
                  <TranslationDrawerForm
                    initialValue={
                      nameTranslation &&
                      nameTranslationHelper.getTranslation(nameTranslation, key)
                    }
                    form={form}
                    formName={getFormName(translationType)}
                    formKey={nameTranslationHelper.getFormKey(key)}
                    onResetTranslation={() =>
                      onResetTranslation("name", key, defaultValue)
                    }
                    isAutoTranslate={
                      isAutoTranslationEnabled &&
                      menuTranslationSetting.translationModelSupportedLanguages?.includes(
                        key,
                      )
                    }
                    isMandatory={menuTranslationSetting.translationFieldMandatoryLanguages?.includes(
                      key,
                    )}
                    disabled={disabled || isLoadingNameTranslation(key)}
                    loading={isLoadingNameTranslation(key)}
                  />
                </div>
                {translationType === TranslationType.item && (
                  <div className={styles.translationForm}>
                    <TranslationDrawerForm
                      initialValue={
                        descriptionTranslation &&
                        descriptionTranslationHelper.getTranslation(
                          descriptionTranslation,
                          key,
                        )
                      }
                      form={form}
                      formName={textMap.descriptionLabel}
                      formKey={descriptionTranslationHelper.getFormKey(key)}
                      onResetTranslation={() =>
                        onResetTranslation(
                          "description",
                          key,
                          defaultValueDescription,
                        )
                      }
                      isAutoTranslate={
                        isAutoTranslationEnabled &&
                        menuTranslationSetting.translationModelSupportedLanguages?.includes(
                          key,
                        )
                      }
                      disabled={
                        disabled || isLoadingDescriptionTranslation(key)
                      }
                      loading={isLoadingDescriptionTranslation(key)}
                    />
                  </div>
                )}
              </div>
            ),
          )}
        </div>
        <div className={styles.drawerFooter}>
          <Button
            type="primary"
            block
            data-testid="btn-submit"
            onClick={handleSave}
            disabled={hasFormErrors(form) || disabled}
          >
            {textMap.confirm}
          </Button>
        </div>
      </Drawer>
    </div>
  )
}

export const TranslationDrawer = Form.create<TranslationDrawerProps>({})(
  TranslationDrawerComponent,
)
